import {
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { RefreshOutlined } from '@material-ui/icons'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { FormDetails } from 'components/ProjectPage/BillOfMaterials/RowDetails/FormDetails'
import { BomItemWorkingStepSummary } from 'features/BillOfMaterials/components/BomItemWorkingStepSummary'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { MultiplyMoney } from 'model/Money'
import { BoMItemType } from 'model/Project/BoMItemRow'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { ReactComponent as NestingRunningIcon } from '_icons/processing/nesting_ani.svg'
import { useBomItemDetailsDrawerStyles } from '../BomItemDetailsDrawer.styles'
import { FinancialDetailsForm } from '../FinancialDetailsForm'
import { FinancialDetailsGraph } from '../Graph/FinancialDetailsGraph'
import { useFinancialDetails } from '../useBomItemDetails'
import { BomItemDetailsProps } from './ProjectDetails/ProjectDetails'

export const BomItemPriceContainer = (props: BomItemDetailsProps) => {
  const sharedClasses = useBomItemDetailsDrawerStyles()
  const { t } = useTranslation()

  const isProjectNesting = useAppSelector(
    projectSelectors.projectIsNestingSelector
  )

  const {
    loading,
    summarizedPriceDetails,
    priceDetails,
    getItemDetails,
    financialViewUnit,
    handleChangeFinancialView,
    totalProjectQuantity,
    bomItemFinancial,
    handleResetProductionTime,
    handleResetAdditionalCosts,
    handleSaveProductionTime,
    handleSaveAdditionalCosts,
    formDetails,
  } = useFinancialDetails({
    bomItemId: props.bomItemId,
    open: true,
  })

  if (isProjectNesting) {
    return (
      <>
        <div
          className={sharedClasses.loading}
          style={{ display: isProjectNesting ? 'flex' : 'none' }}
        >
          <NestingRunningIcon width="72px" />
        </div>
        {formDetails?.length > 0 && (
          <div className={sharedClasses.formDetails}>
            <FormDetails formDetails={formDetails} />
          </div>
        )}
      </>
    )
  }

  const isPriceDetailsEmpty =
    !priceDetails ||
    priceDetails?.findIndex((x) => x.costPrice.value > 0) === -1

  return (
    <>
      <LoadingContainer
        style={{ height: '80vh' }}
        loading={isPriceDetailsEmpty && loading['get-item-details']}
        noMessage
        empty={
          isPriceDetailsEmpty &&
          !isProjectNesting && (
            <>
              <LocalizedTypography
                translationKey={'project:prices-not-calculated'}
              >
                prices are not calculated for this item. Check the item material
                and working steps
              </LocalizedTypography>
              <IconButton onClick={() => getItemDetails()} size="small">
                {loading['get-item-details'] ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshOutlined />
                )}
              </IconButton>
            </>
          )
        }
        emptyBox={{
          height: '30vh',
        }}
      >
        <div style={{ display: !isProjectNesting ? 'initial' : 'none' }}>
          <div className={sharedClasses.actions}>
            <BomItemWorkingStepSummary
              bomItemId={props.bomItemId}
              shouldFilter
              includeCountOfWorkingSteps={
                props.bomItemType === BoMItemType.assemblyHeader
              }
              includeAssemblyWorkingSteps={false}
            />
            <TextField
              select
              value={financialViewUnit}
              label="scope"
              SelectProps={{
                onChange: (ev) =>
                  handleChangeFinancialView(
                    ev.target.value as 'perUnit' | 'total'
                  ),
                renderValue: (value) => (
                  <Typography variant="body2" color="textPrimary">
                    {value === 'perUnit'
                      ? t('common:perUnit', 'per unit')
                      : t('common:total') + ` (x${totalProjectQuantity})`}
                  </Typography>
                ),
              }}
            >
              <MenuItem value={'perUnit'}>
                {t('common:perUnit', 'per unit')}
              </MenuItem>
              <MenuItem value={'total'}>
                {t('common:total') + ` (x${totalProjectQuantity})`}
              </MenuItem>
            </TextField>
            <IconButton onClick={() => getItemDetails()} size="small">
              {loading['get-item-details'] ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshOutlined />
              )}
            </IconButton>
          </div>
          <div className={sharedClasses.graph}>
            <FinancialDetailsGraph
              bomItemId={props.bomItemId}
              priceDetails={summarizedPriceDetails}
              open={true}
              targetPrice={
                financialViewUnit === 'perUnit'
                  ? bomItemFinancial?.targetSalesPricePerItem
                  : MultiplyMoney(
                      bomItemFinancial?.targetSalesPricePerItem,
                      bomItemFinancial?.totalProjectQuantity
                    )
              }
              bomItemType={props.bomItemType}
            />
          </div>

          <FinancialDetailsForm
            bomItemId={props.bomItemId}
            priceDetails={priceDetails}
            summarizedPriceDetails={summarizedPriceDetails}
            financialUnit={financialViewUnit}
            handleResetProductionTime={handleResetProductionTime}
            handleResetAdditionalCosts={handleResetAdditionalCosts}
            handleSaveAdditionalCostPrice={handleSaveAdditionalCosts}
            handleSaveProductionTime={handleSaveProductionTime}
            loading={loading}
          />
        </div>
      </LoadingContainer>
      {formDetails?.length > 0 && (
        <div className={sharedClasses.formDetails}>
          <FormDetails formDetails={formDetails} />
        </div>
      )}
    </>
  )
}
